import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL || "https://interventi.synology.me:5050";

const PreventivoDetail = () => {
  const { id } = useParams();
  const [preventivo, setPreventivo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPreventivo = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_URL}/preventivi/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setPreventivo(response.data);
        console.log("📄 Preventivo detail ricevuto:", {
          id: response.data?.id,
          materiali_len: Array.isArray(response.data?.materiali)
            ? response.data.materiali.length
            : "n/a",
          agg_len: Array.isArray(response.data?.materialiAggiuntivi)
            ? response.data.materialiAggiuntivi.length
            : "n/a",
          escludiIva: response.data?.escludiIva,
        });
      } catch (error) {
        console.error(
          "❌ Errore durante il recupero del preventivo:",
          error.response ? error.response.data : error.message
        );
        toast.error("Errore nel caricamento del preventivo!");
      } finally {
        setIsLoading(false);
      }
    };
    fetchPreventivo();
  }, [id]);

  if (isLoading) return <div>Caricamento...</div>;
  if (!preventivo) return <div>Preventivo non trovato</div>;

  // --- parsing materiali (compatibile array o JSON string) ---
  const safeParse = (val, fallback = []) => {
    try {
      if (Array.isArray(val)) return val;
      if (typeof val === "string" && val.trim() !== "") return JSON.parse(val);
      return fallback;
    } catch (e) {
      console.error("Parse error:", e);
      return fallback;
    }
  };

  const isAgg = (v) =>
    v === 1 || v === "1" || v === true || String(v).toLowerCase() === "true";

  const allMat = safeParse(preventivo.materiali, []);
  let inclusi = allMat.filter((m) => !isAgg(m.is_aggiuntivo));
  let aggiuntivi = allMat.filter((m) => isAgg(m.is_aggiuntivo));

  // se il backend invia ancora l'array separato, uniscilo agli aggiuntivi
  const extra = safeParse(preventivo.materialiAggiuntivi, []);
  if (extra.length) {
    aggiuntivi = [...aggiuntivi, ...extra];
  }

  const renderTabella = (rows) => {
    if (!rows || rows.length === 0) return <p>Nessun materiale</p>;
    return (
      <table className="interventi-table">
        <thead>
          <tr>
            <th>Miniatura</th>
            <th>Codice</th>
            <th>Descrizione</th>
            <th>Quantità</th>
            <th>Costo Unitario (€)</th>
            <th>Costo Totale (€)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m, i) => {
            const costoUnitario = parseFloat(m.costoUnitario ?? m.prezzoSenzaIva ?? 0) || 0;
            const quantita = parseInt(m.quantita, 10) || 0;
            const costoTotale = quantita * costoUnitario;
            return (
              <tr key={i}>
                <td>
                  {m.thumbnail ? (
                    <img
                      src={`${API_URL}${m.thumbnail}`}
                      alt={m.descrizioneMateriale || m.descrizione || "Materiale"}
                      style={{ width: "50px", height: "50px", objectFit: "cover" }}
                      onError={() =>
                        toast.warn(`Impossibile caricare la miniatura per ${m.codiceMateriale || "N/D"}`)
                      }
                    />
                  ) : (
                    "Nessuna miniatura"
                  )}
                </td>
                <td>{m.codiceMateriale || "N/A"}</td>
                <td>{m.descrizioneMateriale || m.descrizione || "N/A"}</td>
                <td>{quantita}</td>
                <td>{costoUnitario.toFixed(2)}</td>
                <td>{costoTotale.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div>
      <h3 className="interventi-title">Dettagli Preventivo #{preventivo.id}</h3>
      <Link
        to="/dashboard/preventivi"
        className="back-btn"
        style={{ marginBottom: "20px", display: "inline-block" }}
      >
        ← Torna ai Preventivi
      </Link>

      <div className="modal-section">
        <h4>Dati Cliente</h4>
        <p><strong>Cliente:</strong> {preventivo.cliente || "N/A"}</p>
        <p><strong>Indirizzo:</strong> {preventivo.indirizzo || "N/A"}</p>
        <p><strong>Email:</strong> {preventivo.email || "N/A"}</p>
        <p><strong>Telefono:</strong> {preventivo.telefono || "N/A"}</p>
        <p><strong>Codice Fiscale:</strong> {preventivo.codiceFiscale || "N/A"}</p>
        <p><strong>Partita IVA:</strong> {preventivo.partitaIva || "N/A"}</p>
      </div>

      <div className="modal-section">
        <h4>Dati Preventivo</h4>
        <p>
          <strong>Data Emissione:</strong>{" "}
          {preventivo.dataEmissione
            ? new Date(preventivo.dataEmissione).toLocaleDateString("it-IT")
            : "N/A"}
        </p>
        <p><strong>Validità:</strong> {preventivo.validita || "30"} giorni</p>
        <p><strong>Descrizione:</strong> {preventivo.descrizione || "N/A"}</p>
        <p><strong>Stato:</strong> {preventivo.stato || "Da fare"}</p>
      </div>

      {/* Materiali inclusi */}
      <div className="modal-section">
        <h4>Materiali (inclusi nel preventivo)</h4>
        {renderTabella(inclusi)}
      </div>

      {/* Materiali aggiuntivi */}
      <div className="modal-section">
        <h4>Materiali aggiuntivi (fuori preventivo)</h4>
        {renderTabella(aggiuntivi)}
      </div>

      <div className="modal-section">
        <h4>Costi</h4>
        <p>
          <strong>Costo Materiali:</strong>{" "}
          {parseFloat(preventivo.costoMateriali || 0).toFixed(2)} €
        </p>
        <p>
          <strong>Costo Manodopera:</strong>{" "}
          {parseFloat(preventivo.costoManodopera || 0).toFixed(2)} € (Ore:{" "}
          {preventivo.oreLavoro || 0}, Costo Orario:{" "}
          {parseFloat(preventivo.costoOrario || 0).toFixed(2)} €)
        </p>
        <p>
          <strong>Costi Aggiuntivi:</strong>{" "}
          {parseFloat(preventivo.costiAggiuntivi || 0).toFixed(2)} €
        </p>
        <p>
          <strong>Sconto:</strong> {parseFloat(preventivo.scontoPercentuale || 0)}% (
          {parseFloat(preventivo.scontoImporto || 0).toFixed(2)} €)
        </p>
        <p>
          <strong>Costo Totale (escl. IVA):</strong>{" "}
          {parseFloat(preventivo.costoTotale || 0).toFixed(2)} €
        </p>
        <div style={{ margin: "10px 0" }}>
  <p style={{ margin: 0, fontSize: "14px", color: "#555" }}>
    <strong>Uso prezzo materiali comprensivo IVA:</strong>{" "}
    gestito nel form di modifica preventivo.
  </p>
</div>
        <p><strong>Escludi IVA:</strong> {preventivo.escludiIva ? "Sì" : "No"}</p>
        {!preventivo.escludiIva && (
          <p>
            <strong>IVA ({parseFloat(preventivo.ivaPercentuale || 22)}%):</strong>{" "}
            {(
              parseFloat(preventivo.costoTotaleConIva || 0) -
              parseFloat(preventivo.costoTotale || 0)
            ).toFixed(2)} €
          </p>
        )}
        <p>
          <strong>Costo Totale (incl. IVA):</strong>{" "}
          {parseFloat(preventivo.costoTotaleConIva || 0).toFixed(2)} €
        </p>
      </div>

      <div className="modal-section">
        <h4>Condizioni</h4>
        <p><strong>Tempi di Esecuzione:</strong> {preventivo.tempiEsecuzione || "N/A"}</p>
        <p><strong>Condizioni di Pagamento:</strong> {preventivo.condizioniPagamento || "N/A"}</p>
        <p><strong>Garanzia:</strong> {preventivo.garanzia || "N/A"}</p>
      </div>

      <div className="modal-section">
        <h4>Allegati</h4>
        {preventivo.allegati && preventivo.allegati.length > 0 ? (
          <ul>
            {preventivo.allegati.map((allegato, index) => (
              <li key={index}>
                {allegato.descrizione || allegato.filename} ({allegato.tipo})
              </li>
            ))}
          </ul>
        ) : (
          <p>Nessun allegato</p>
        )}
      </div>

      <div className="modal-section">
        <h4>Note</h4>
        <p>{preventivo.note || "Nessuna nota"}</p>
      </div>
    </div>
  );
};

export default PreventivoDetail;
